import gsap from 'gsap';
import CustomEase from 'gsap/CustomEase';
import { trap_focus, focusableElementsString } from '../utils/trap-focus';

gsap.registerPlugin(CustomEase);

const ANIMATION_DURATION = 0.3;
const BREAKPOINTS = {
    mobile: 768
};

// Configuration de l'ease personnalisé pour une animation plus fluide
const customEase = CustomEase.create("custom", "M0,0 C0.25,0.1 0.25,1 1,1");

let isMobile = window.innerWidth <= BREAKPOINTS.mobile;

const PANEL_TYPES = {
    AGENDA: 'agenda',
    SEARCH_HEADER: 'search-header',
    FILTERS: 'filters-js'
};

const SVG_ICONS = {
    agenda: {
        open: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="13.9497" y="15.364" width="16" height="2" transform="rotate(-135 13.9497 15.364)" fill="currentColor"/>
            <rect x="2.63599" y="13.9498" width="16" height="2" transform="rotate(-45 2.63599 13.9498)" fill="currentColor"/>
        </svg>`,
        close: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 3H11V1H13V3H16V16H2V3H5V1H7V3ZM4 9V14H14V9H4ZM4 7H14V5H4V7Z" fill="currentColor"/>
        </svg>`
    },
    'search-header': {
        open: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="13.9497" y="15.364" width="16" height="2" transform="rotate(-135 13.9497 15.364)" fill="currentColor"/>
            <rect x="2.63599" y="13.9498" width="16" height="2" transform="rotate(-45 2.63599 13.9498)" fill="currentColor"/>
        </svg>`,
        close: `<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.62402 1.00781C10.9527 1.17652 13.5996 3.92923 13.5996 7.2998L13.5918 7.62402C13.5282 8.87834 13.0975 10.0357 12.4053 10.9912L16.5049 15.0908L15.0908 16.5049L10.9902 12.4043C9.95286 13.1555 8.67848 13.5996 7.2998 13.5996L6.97559 13.5918C3.75433 13.4285 1.17108 10.8453 1.00781 7.62402L1 7.2998C1.00002 3.82053 3.82053 1.00002 7.2998 1L7.62402 1.00781ZM7.2998 3C4.9251 3.00003 3.00003 4.9251 3 7.2998C3 9.67453 4.92508 11.5996 7.2998 11.5996C9.67455 11.5996 11.5996 9.67455 11.5996 7.2998C11.5996 4.92508 9.67453 3 7.2998 3Z" fill="currentColor"/>
        </svg>`
    }
};

const createSVG = (iconName) => {
    return `<svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <use href="#icon-${iconName}" />
    </svg>`;
};

const updatePanelWidth = (panel) => {
    if (!panel) return;
    
    const width = isMobile ? '100%' : '50%';
    
    gsap.fromTo(panel, {
        width: 0,
        transformOrigin: 'right'
    },{
        width: width,
        duration: ANIMATION_DURATION,
        ease: customEase,
        overwrite: true
    });
};

// Fonction de gestion du redimensionnement
const handleResize = () => {
    const wasMobile = isMobile;
    isMobile = window.innerWidth <= BREAKPOINTS.mobile;

    if (wasMobile !== isMobile) {
        const overlayPannel = document.querySelector('.component-pannel-overlay__pannel.is-visible');
        if (overlayPannel) {
            updatePanelWidth(overlayPannel);
        }
    }
};

// Gestionnaire de redimensionnement avec debounce
let resizeTimeout;
const initResizeHandler = () => {
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(handleResize, 100);
    });
};

const initIndependentPannels = () => {
    document.addEventListener('click', event => {
        const targetButton = event.target.closest('[data-pannel-target][data-pannel-independent]');
        if (!targetButton) return;

        if (targetButton.getAttribute('data-pannel-close') !== null) {
            closeIndependent(targetButton);
            return;
        }

        const pannelId = targetButton.getAttribute('data-pannel-target');
        const pannel = document.querySelector(`#${pannelId}`);
        if (!pannel) return;

        if (document.querySelector('.component-pannel.is-visible[data-pannel-independent]')) {
            return;
        }

        if (pannel.classList.contains('is-visible')) {
            closeIndependent(targetButton);
        } else {
            openIndependent(targetButton, pannel);
        }
    });
};

const openIndependent = (button, pannel) => {
    button.classList.add('active');
    
    let overlayElement = document.querySelector(`.component-pannel-overlay--${pannel.id}`);
    if (!overlayElement) {
        overlayElement = document.createElement('div');
        overlayElement.className = `component-pannel-overlay--${pannel.id}`;
        document.body.appendChild(overlayElement);
    }

    const width = isMobile ? '100%' : '50%';
    
    gsap.fromTo(pannel, 
        {
            width: 0,
            opacity: 1
        },
        {
            width: width,
            duration: ANIMATION_DURATION,
            ease: customEase,
            onStart: () => {
                pannel.classList.add('is-visible');
                overlayElement.classList.add('is-visible');
            }
        }
    );
};

const closeIndependent = (button) => {
    const pannelId = button.getAttribute('data-pannel-target');
    const pannel = document.querySelector(`#${pannelId}`);
    if (!pannel) return;

    button.classList.remove('active');
    
    const overlayElement = document.querySelector(`.component-pannel-overlay--${pannelId}`);
    
    gsap.to(pannel, {
        width: 0,
        duration: ANIMATION_DURATION,
        ease: customEase,
        onComplete: () => {
            pannel.classList.remove('is-visible');
            if (overlayElement) {
                overlayElement.classList.remove('is-visible');
            }
        }
    });
};

const initDefaultPannels = () => {
    const headroom = document.querySelector('.component-headroom');
    const overlay = document.querySelector('.component-pannel-overlay');
    const overlayPannel = document.querySelector('.component-pannel-overlay__pannel');
    let previous_button = null;
    let current_button = null;

    // Configuration de l'animation principale
    const timeline = gsap.timeline({
        paused: true,
        onStart: () => {
            overlay.classList.add('is-visible');
            overlayPannel.classList.add('is-visible');
            
            // Définir la largeur initiale
            gsap.set(overlayPannel, { 
                width: 0
            });
        },
        onReverseComplete: () => {
            overlay.classList.remove('is-visible');
            overlayPannel.classList.remove('is-visible');
            overlayPannel.classList.remove('pannel-white');
            headroom.classList.remove('pannel-open');
            headroom.classList.remove('pannel-open-white');
            updateButtonSVG(null);
        }
    });

    // Configuration des animations
    timeline
        .to(overlay, {
            opacity: 1,
            duration: ANIMATION_DURATION,
            ease: 'power2.inOut'
        }, 0)
        .to(overlayPannel, {
            width: isMobile ? '100%' : '50%',
            duration: ANIMATION_DURATION,
            ease: customEase
        }, 0);

    // Initialisation des pannels
    document.querySelectorAll('.component-pannel:not([data-pannel-independent])').forEach(element => {
        if (!element.classList.contains('component-pannel--init')) {
            element.classList.add('component-pannel--init');
        }
    });

    // Gestionnaire d'événements pour les clics
    document.addEventListener('click', event => {
        const targetButton = event.target.closest('[data-pannel-target]:not([data-pannel-independent])');
        if (!targetButton) {
            // Si le clic est sur component-pannel-overlay, fermer les pannels
            if (event.target.classList.contains('component-pannel-overlay')) {
                document.querySelectorAll('.component-pannel.is-visible').forEach(pannel => {
                    const pannelId = pannel.id;
                    const activeButton = document.querySelector(`[data-pannel-target="${pannelId}"].active`);
                    close(activeButton)
                });
                return;
            }
            return;
        }

        if (targetButton.getAttribute('data-pannel-close') !== null) {
            close(targetButton);
            return;
        }

        const button = targetButton;
        previous_button = current_button;
        current_button = button.getAttribute('data-pannel-target');

        if (previous_button === current_button && button.classList.contains('active')) {
            close(button);
        } else {
            if (overlayPannel.classList.contains('is-visible')) {
                switch_pannel(previous_button, button);
            } else {
                open(button);
            }
        }
    });

    // Fermer les pannels lors d'un changement de page
    document.addEventListener('click', event => {
        const link = event.target.closest('a');
        if (link && !link.getAttribute('data-pannel-target')) {
            document.querySelectorAll('.component-pannel.is-visible').forEach(pannel => {
                const pannelId = pannel.id;
                const activeButton = document.querySelector(`[data-pannel-target="${pannelId}"].active`);
                close(activeButton);
            });
        }
    });

    const open = button => {
        const pannel_id = button.getAttribute('data-pannel-target');
        const pannel = document.querySelector(`#${pannel_id}`);
        if (!pannel) return;

        button.classList.add('active');
        updatePanelStyles(pannel_id);
        updateButtonSVG(pannel_id);

        timeline.play();
        pannel.classList.add('is-visible');

        gsap.fromTo(pannel, {
            width: 0,
            opacity: 1,
        }, {
            width: isMobile ? '100%' : '50%',
            duration: ANIMATION_DURATION,
            ease: customEase
        });
    };

    const close = button => {
        if (!button) return;

        const pannel_id = button.getAttribute('data-pannel-target');
        const pannel = document.querySelector(`#${pannel_id}`);
        if (!pannel) return;

        document.querySelectorAll(`[data-pannel-target="${pannel_id}"].active`)
            .forEach(activeButton => activeButton.classList.remove('active'));

        button.classList.remove('active');
        updatePanelStyles(null);
        updateButtonSVG(null);

        timeline.reverse();
        gsap.fromTo(pannel, {
            width: isMobile ? '100%' : '50%',
            opacity: 1,
        }, {
            width: 0,
            duration: ANIMATION_DURATION,
            ease: customEase,
            onComplete: () => {
                pannel.classList.remove('is-visible');
                headroom.classList.remove('pannel-open');
                headroom.classList.remove('pannel-open-white');
            }
        });
    };

    const switch_pannel = (previous_button, current_button) => {
        const current_pannel_id = current_button.getAttribute('data-pannel-target');
        const previous_pannel_id = previous_button;

        const previous_pannel = document.getElementById(previous_pannel_id);
        const current_pannel = document.getElementById(current_pannel_id);

        if (!previous_pannel || !current_pannel) return;

        document.querySelector(`button[data-pannel-target="${previous_pannel_id}"]`)
            .classList.remove('active');
        current_button.classList.add('active');

        previous_pannel.classList.remove('is-visible');
        current_pannel.classList.add('is-visible');

        updatePanelStyles(current_pannel_id);
        updateButtonSVG(current_pannel_id);

        gsap.fromTo(previous_pannel, {
            opacity: 1,
        }, {
            opacity: 0,
            duration: ANIMATION_DURATION,
            ease: customEase,
            onComplete: () => previous_pannel.classList.remove('is-visible')
        });

        gsap.fromTo(current_pannel, {
            opacity: 0,
            width: isMobile ? '100%' : '50%',
        }, {
            opacity: 1,
            width: isMobile ? '100%' : '50%',
            duration: ANIMATION_DURATION,
            ease: customEase,
            onComplete: () => current_pannel.classList.add('is-visible')
        });
    };

    const updatePanelStyles = (pannel_id) => {
        const headroom = document.querySelector('.component-headroom');
        const overlayPannel = document.querySelector('.component-pannel-overlay__pannel');
        
        // Gestion des classes pour le headroom
        if (pannel_id === PANEL_TYPES.AGENDA) {
            headroom.classList.add('pannel-open');
            headroom.classList.add('pannel-open-white');
            overlayPannel.classList.add('pannel-white');
        } else if (pannel_id) {
            headroom.classList.add('pannel-open');
            headroom.classList.remove('pannel-open-white');
            overlayPannel.classList.remove('pannel-white');
        } else {
            headroom.classList.remove('pannel-open');
            headroom.classList.remove('pannel-open-white');
            overlayPannel.classList.remove('pannel-white');
        }
    };

    const updateButtonSVG = (pannel_id) => {
        // Réinitialiser tous les boutons
        Object.values(PANEL_TYPES).forEach(type => {
            const buttons = document.querySelectorAll(`[data-pannel-target="${type}"]`);
            buttons.forEach(button => {
                const iconContainer = button.querySelector('.component__icon');
                if (iconContainer && SVG_ICONS[type]) {
                    iconContainer.innerHTML = SVG_ICONS[type].close;
                }
            });
        });

        // Mettre à jour le bouton actif si nécessaire
        if (pannel_id && SVG_ICONS[pannel_id]) {
            const activeButton = document.querySelector(`[data-pannel-target="${pannel_id}"].active`);
            if (activeButton) {
                const iconContainer = activeButton.querySelector('.component__icon');
                if (iconContainer) {
                    iconContainer.innerHTML = SVG_ICONS[pannel_id].open;
                }
            }
        }
    };
};

const init_pannel = () => {
    initIndependentPannels();
    initDefaultPannels();
    initResizeHandler();
};

document.addEventListener('ContentLoaded', init_pannel);